import {
  APP_BASE_HREF,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import {
  FREELANCER_HTTP_CONFIG,
  FreelancerHttpConfig,
} from '@freelancer/freelancer-http';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { ErrorTracking, PerformanceTracking } from '@freelancer/tracking';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Response } from 'express';
import { firstValueFrom } from 'rxjs';
import { RESPONSE } from '../../express.tokens';

export interface RedirectToPhpOptions {
  // If set to false, will not replace current url when redirect to php.
  isInPageGuard: boolean;
}

@UntilDestroy({ className: 'RedirectToPhp' })
@Injectable({
  providedIn: 'root',
})
export class RedirectToPhp {
  private pwa = inject(Pwa);
  private auth = inject(Auth);
  private performanceTracking = inject(PerformanceTracking);
  private freelancerHttpConfig = inject<FreelancerHttpConfig>(
    FREELANCER_HTTP_CONFIG,
  );
  private location = inject(Location);
  private platformId = inject(PLATFORM_ID);
  private baseHref = inject(APP_BASE_HREF, { optional: true });
  private response = inject<Response>(RESPONSE, { optional: true });
  private errorTracking = inject(ErrorTracking);
  private router = inject(Router);

  doRedirect(url: string, options?: RedirectToPhpOptions): Promise<boolean> {
    // Add ?w=f (webapp false) search param to the URL in order to force the
    // PHP version on routes serving both webapp & php pages
    // Also add referrer=webapp to track potential redirect loops
    let redirectUrl: string;
    const u = this.baseHref
      ? new URL(
          `${this.location.origin}${this.baseHref.replace(/\/$/, '')}${url}`,
        )
      : new URL(`${this.location.origin}${url}`);
    let redirectTimes = 0;
    if (u.searchParams.get('w') === 'f') {
      redirectTimes =
        parseInt(u.searchParams.get('redirect-times') ?? '0', 10) ?? 0;
      if (redirectTimes >= 5) {
        this.errorTracking.captureMessage(
          'Webapp to PHP redirect loop, redirect happened at least 5 times.',
          { currentUrl: this.router.url, redirectTo: url, redirectTimes },
        );
      }
    }
    const s = u.searchParams;
    s.set('w', 'f');
    s.set('redirect-times', `${redirectTimes + 1}`);
    u.search = s.toString();
    redirectUrl = u.toString();
    if (isPlatformBrowser(this.platformId)) {
      // Log the redirect time
      console.log(`redirected in ${Math.round(window.performance.now())} ms`);
      this.performanceTracking.mark('webapp_php_redirect', true);
    }
    // When in localhost, redirect to the freelancerHttpConfig.baseUrl
    // and transfer auth state using the internal auth/callback route.
    const isLocalhost =
      this.location.hostname === 'localhost' ||
      this.location.hostname.startsWith('192.168.') ||
      this.location.hostname.startsWith('10.');

    // Make legacy pages redirect internally so that there are no unnecessary 302s
    if (isPlatformServer(this.platformId)) {
      this.response?.set('X-Varnish-Follow-Redirect', 'true');
    }

    const replaceUrl = options?.isInPageGuard ?? true;
    if (
      isLocalhost &&
      // This doesn't need to be done in native apps because
      // redirect() does it already when opening the in-app browser
      !this.pwa.isNative()
    ) {
      return firstValueFrom(
        this.auth.authState$.pipe(untilDestroyed(this)),
      ).then(state => {
        redirectUrl = `${u.pathname}${u.search}${u.hash}`;
        if (state) {
          return this.location.redirect(
            `${this.freelancerHttpConfig.baseUrl}/internal/auth/callback?uid=${
              state.userId
            }&token=${encodeURIComponent(
              state.token,
            )}&next=${encodeURIComponent(redirectUrl)}`,
            replaceUrl,
          );
        }
        return this.location.redirect(
          `${this.freelancerHttpConfig.baseUrl}${redirectUrl}`,
          replaceUrl,
        );
      });
    }
    return this.location.redirect(redirectUrl, replaceUrl);
  }
}
